import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getEnrichedTeamsData, teamMemberHref, teamTabFromParam, teamTabToParam, teams } from '../../models/content';

const LEADERSHIP_ORDER = ['rajesh-sharma', 'sanjay-jain', 'pr-srinivasan', 'devinjit-singh'];

function sortMembers(members, activeKey) {
  const deduped = [];
  const seen = new Set();
  members.forEach((m) => {
    if (seen.has(m.slug)) return;
    seen.add(m.slug);
    deduped.push(m);
  });

  if (activeKey !== 'Leadership Teams') return deduped;

  return [...deduped].sort((a, b) => {
    const ai = LEADERSHIP_ORDER.indexOf(a.slug);
    const bi = LEADERSHIP_ORDER.indexOf(b.slug);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

function TeamCard({ member, activeKey }) {
  return (
    <Link
      className="team-card-link"
      to={teamMemberHref(member.slug, activeKey)}
      aria-label={`View profile of ${member.name}`}
    >
      <article className="team-card">
        <div
          className="portrait"
          style={{
            backgroundImage: member.image ? `url("${member.image}")` : undefined,
            backgroundColor: member.image ? undefined : 'var(--p100)',
          }}
        />
        <div className="meta">
          <h3 className="name">{member.name}</h3>
          <div className="role">{member.role}</div>
          {member.bio ? <p className="bio">{member.bio}</p> : null}
        </div>
      </article>
    </Link>
  );
}

export default function V2TeamsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeKey = teamTabFromParam(searchParams.get('tab'));
  const enrichedData = useMemo(() => getEnrichedTeamsData(), []);

  const setActiveKey = (key) => {
    setSearchParams({ tab: teamTabToParam(key) }, { replace: true });
  };

  const members = useMemo(() => {
    const list = enrichedData[activeKey] ?? [];
    return sortMembers(Array.isArray(list) ? list : [], activeKey);
  }, [activeKey, enrichedData]);

  return (
    <main>
      <section className="page-hero">
        <div className="wrap">
          <span className="v2-badge">
            <span className="dot" />
            {teams.heroBadge}
          </span>
          <h1>
            {teams.heroTitle.map((p, i) =>
              typeof p === 'string' ? (
                <React.Fragment key={i}>{p}</React.Fragment>
              ) : (
                <span key={i} className="it">
                  {p.it}
                </span>
              )
            )}
          </h1>
        </div>
      </section>

      <section>
        <div className="wrap">
          <p className="teams-intro">
            {teams.intro.split('100 years').map((chunk, idx, arr) => (
              <React.Fragment key={idx}>
                {chunk}
                {idx < arr.length - 1 ? <strong>100 years</strong> : null}
              </React.Fragment>
            ))}
          </p>

          <div className="tabs-row">
            <div className="tabs" role="tablist" aria-label="Teams tabs">
              {teams.tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`tab ${activeKey === t.key ? 'is-active' : ''}`}
                  role="tab"
                  aria-selected={activeKey === t.key}
                  onClick={() => setActiveKey(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div key={activeKey} className="team-grid team-grid-animated">
            {members.map((m) => (
              <TeamCard key={m.slug} member={m} activeKey={activeKey} />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
